/* -------------------------- Design imports start -------------------------- */
import { DeleteOutlined, EditOutlined, InfoOutlined } from '@mui/icons-material'
import { Stack, Tooltip } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useCallback, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import ArticlesExpandedRow from './ArticlesExpandedRow'
import CreateRequestSharepointDrawer from '../Sharepoint/Drawer/CreateRequestSharepointDrawer'
import RequestDrawer from '../Request/Drawer/RequestDrawer'
import RequestStaffDrawer from '../Request/Drawer/RequestStaffDrawer'
import { RequestInfoPanelContent } from './RequestInfoPanel'
import Button from '../../../../../components/Button'
import FilesDrawer from '../../../../Filesystem/components/FilesDrawer'
import SmartTable from '../../../../../components/SmartTable'
import DeleteModal from '../../../../../components/widgets/DeleteModal'
import { useQueryClient } from '@tanstack/react-query'

import LoadingSkeleton from '../../../../../components/widgets/LoadingSkeleton'
import LogTool from '../../../../../logger/logTools'
import { useCountContext, useUserContext } from '../../../../../utils/context'
import { canDelete, isPrivileged } from '../../../../../utils/functions'
import GetConstant from '../../../utils/constants'
import { type Request, type RequestFormInput } from '../../../utils/types'
import CreateOfferDrawer from '../../../Offer/CreateOfferDrawer'
import {
  type FileCollection,
  type FileInfo,
  type SmartTableColumnType,
} from '../../../../../utils/types'
import { inputToAPISharepointJSON } from '../../../../Item/api/articleApi'
import { type Sharepoint } from '../../../../Item/utils/types'
import {
  useDeleteRequests,
  usePatchRequest,
  useCreateOffer,
} from '../../../hooks/useRequestMutations'
import { useRequests } from '../../../hooks/useRequestQueries'
import { requestKeys } from '../../../utils/queryKeys'
import { useCreateSharepoint } from '../../../../Sharepoint/hooks/useSharepointMutations'
import { markEntityAsRead } from '../../../../../api/unreadOverview'
import { inputToAPIOfferJSON } from '../../../api/requestApi'
import { PERMISSIONS } from '../../../../../config/permissions'
import { type RequestFilters } from '../../../api/requestApi'
import { useHistory } from 'react-router-dom'
/* ------------------------- Functional import end ------------------------- */

type Props = {
  selectedRequest: Request | undefined
  setSelectedRequest: React.Dispatch<React.SetStateAction<Request | undefined>>
  openFilesDrawer: boolean
  setOpenFilesDrawer: React.Dispatch<React.SetStateAction<boolean>>
}

/* -------------------------------------------------------------------------- */
/*                               Start component                              */
/* -------------------------------------------------------------------------- */
export default function RequestOverviewPage(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'RequestOverviewPage', enable: true, logLevel: 'debug' })
  const { t } = useTranslation()
  const { selectedRequest, setSelectedRequest, openFilesDrawer, setOpenFilesDrawer } = props
  const history = useHistory()
  const { user, userPermissions } = useUserContext()
  const {
    initialRequests,
    setInitialRequests,
    setInitialRequestsCount,
    setCountTotals,
    setTabValue,
    requestQueryOptions,
    setRequestQueryOptions,
  } = useCountContext()
  const queryClient = useQueryClient()
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [requestDrawerOpen, setRequestDrawerOpen] = useState<boolean>(false)
  const [requestStaffDrawerOpen, setRequestStaffDrawerOpen] = useState<boolean>(false)
  const [offerDrawerOpen, setOfferDrawerOpen] = useState<boolean>(false)
  const [openCreateRequestSharepointDrawer, setOpenCreateRequestSharepointDrawer] =
    useState<boolean>(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [filesDrawerRefreshKey, setFilesDrawerRefreshKey] = useState(0)
  /* ----------------------------- Flag states end ---------------------------- */
  /* --------------------------- Data states start ---------------------------- */
  const [selectedRequests, setSelectedRequests] = useState<Request[]>([])
  const [requestFormInput, setRequestFormInput] = useState<any>({})
  const [requestStaffFormInput, setRequestStaffFormInput] = useState<any>({})
  const [offerFormInput, setOfferFormInput] = useState<any>({})
  const [newRequests, setNewRequests] = useState<Request[]>(initialRequests)
  const [decliningRequestId, setDecliningRequestId] = useState<string | null>(null)
  /* ---------------------------- Data states end ----------------------------- */

  /* ---------------------------- Mutations start ----------------------------- */
  const deleteRequestsMutation = useDeleteRequests()
  const patchRequestMutation = usePatchRequest()
  const createOfferMutation = useCreateOffer()
  const createSharepointMutation = useCreateSharepoint()
  /* ----------------------------- Mutations end ------------------------------ */

  /* ------------------------ Callback functions start ------------------------ */
  async function markRequestAsRead(requestItem: Request): Promise<void> {
    const requestId = Number(requestItem.key)
    if (Number.isNaN(requestId)) {
      log.warn('Unable to resolve request id for marking as read', requestItem)
      return
    }

    // Optimistic update directly on all request list queries
    queryClient.setQueriesData({ queryKey: requestKeys.lists(), exact: false }, (oldData: any) => {
      if (!oldData || !oldData.results) return oldData
      return {
        ...oldData,
        results: oldData.results.map((item: Request) =>
          item.key === requestItem.key ? { ...item, read: true } : item
        ),
      }
    })

    // Update counts for tab badges
    setInitialRequestsCount(prev => Math.max(0, prev - 1))
    setCountTotals(prev => Math.max(0, prev - 1))

    try {
      await markEntityAsRead('request', requestId)
    } catch (error) {
      log.error('Error marking request as read', error)
      toast.error(
        t('request:feedback.error.markAsRead', 'Fehler beim Aktualisieren des Lesestatus')
      )
    }
  }

  async function handleUpdateRequest(
    requestInput: RequestFormInput | undefined
  ): Promise<string | void> {
    log.debug('input ->', requestInput)
    if (!requestInput) {
      throw new Error('Error updating request. No request input provided')
    }

    try {
      const updatedRequest = await patchRequestMutation.mutateAsync({
        idOrSelf: requestInput.self,
        data: requestInput,
      })

      log.info('Success updating request', updatedRequest)
      toast.success(t('request:feedback.success.updateRequest'))
      setSelectedRequests([])
      setSelectedRequest(undefined)
      // Don't set refreshKey here - it will be set after files are uploaded via onFilesUploaded callback
      return typeof updatedRequest.files === 'string' ? updatedRequest.files : undefined
    } catch (error) {
      log.error('Error updating request', error)
      toast.error(t('request:feedback.error.updateRequest'))
      setSelectedRequests([])
      setSelectedRequest(undefined)
    }
  }

  const handleFilesUploaded = () => {
    // Trigger refresh of FilesDrawer after files are uploaded
    setFilesDrawerRefreshKey(prev => prev + 1)
  }

  const handleDeleteRequest = async (): Promise<void> => {
    try {
      const requestsToDelete = [...selectedRequests]

      // Clear selection before mutation to prevent warning about selected items
      // that are no longer visible (mutation's onSuccess invalidates queries)
      flushSync(() => {
        setSelectedRequests([])
        setSelectedRequest(undefined)
        setOpenDelete(false)
      })

      await deleteRequestsMutation.mutateAsync(requestsToDelete)
      log.info('Success deleting Requests')
      toast.success(t('request:feedback.success.deleteRequest'))
    } catch (error) {
      log.error('Error deleting request:', error)
      toast.error(t('request:feedback.error.deleteRequest'))
    }
  }

  const handleCreateRequestSharepoint = async (sharepointInput: any) => {
    log.info('Begin creating sharepoint for request')
    const json = inputToAPISharepointJSON(sharepointInput)
    log.debug('Sharepoint JSON ->', json)

    try {
      const createdSharepoint = await createSharepointMutation.mutateAsync(json)
      log.info('Success creating sharepoint for request', createdSharepoint)
      return createdSharepoint as Sharepoint
    } catch (error) {
      log.error('Error creating sharepoint for request', error)
      return 'ERROR'
    }
  }

  const handleCreateOffer = async (offerInput: any): Promise<void> => {
    const json = inputToAPIOfferJSON({ ...offerInput, status: 0 })
    log.debug('Creating offer -> ', json)

    try {
      const createdOffer = await createOfferMutation.mutateAsync(json)
      log.info('Success creating offer', createdOffer)
      toast.success(t('request:feedback.success.creatingRequest'))
      log.debug('Offer created -> ', createdOffer)
    } catch (error) {
      log.error('Error creating offer', error)
      toast.error(t('request:feedback.error.creatingRequest'))
    }
  }

  const handleOffer = useCallback(
    (event: any, selectedRequest: Request) => {
      log.debug('selectedRequest ->', selectedRequest)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      event.stopPropagation()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      event.preventDefault()
      const newOffer: any = {
        request: selectedRequest,
        prices: {},
      }
      if (Array.isArray(selectedRequest.quantities) && selectedRequest.quantities.length > 0) {
        const newPrices: any = {}
        selectedRequest.quantities.forEach((quantity: number, index: number) => {
          newPrices[quantity] = ''
        })
        newOffer.prices = newPrices
      }
      setOfferFormInput(newOffer)
      setOfferDrawerOpen(true)
    },
    [log]
  )

  const handleDecline = useCallback(
    async (event: any, selectedRequest: Request) => {
      log.debug('selectedRequest ->', selectedRequest)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      event.stopPropagation()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      event.preventDefault()

      setDecliningRequestId(selectedRequest.self)
      try {
        await patchRequestMutation.mutateAsync({
          idOrSelf: selectedRequest.self,
          data: { status: 2 },
        })

        setRequestDrawerOpen(false)
        toast.success(t('request:feedback.success.declineRequest'))
      } catch (error) {
        log.error('Error declining request', error)
        toast.error(t('request:feedback.error.declineRequest'))
      } finally {
        setDecliningRequestId(null)
      }
    },
    [log, patchRequestMutation, t]
  )

  const handleRequestingOffers = useCallback(
    (e: any, record: Request) => {
      // prevent the onRow callback to be fired
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      e.stopPropagation()

      if (typeof record.article === 'string') {
        throw new Error(
          'Missing required property: request.article. Make sure to expand the article ' +
            'property when fetching the requests.'
        )
      }

      setSelectedRequest(record)
      if (record.article.isBomBase) {
        // open the BOM anonymization and sharepoint creation page
        history.push({
          pathname: '/request/create-sharepoint',
          state: { selectedRequest: record },
        })
      } else {
        // legacy: single article request -> open the CreateRequestSharepointDrawer
        setOpenCreateRequestSharepointDrawer(true)
      }
    },
    [selectedRequest]
  )

  /* ------------------------- Callback functions end ------------------------- */
  /* ------------------------- Render constants start ------------------------- */
  // GetConstant calls hooks internally, so it cannot be inside useMemo
  const requestTableColumns = GetConstant({
    name: 'requestTableColumns',
    newRequests: newRequests,
    handleRequestingOffers,
    handleOffer: handleOffer,
    handleDecline: (e, record) => void (async () => await handleDecline(e, record))(),
    decliningRequestId,
  }) as SmartTableColumnType<Request>[]

  const requestTableActions = (
    <Stack direction="row" spacing={1} alignItems="center">
      {isPrivileged(user, 'STAFF') && (
        <Button
          id="editRequestButton"
          size="small"
          color="primary"
          variant="outlined"
          startIcon={<EditOutlined />}
          onClick={() => {
            if (isPrivileged(user, 'STAFF')) {
              setRequestStaffFormInput(selectedRequests[0])
              setRequestStaffDrawerOpen(true)
            } else {
              setRequestFormInput(selectedRequests[0])
              setRequestDrawerOpen(true)
            }
          }}
          disabled={selectedRequests.length !== 1}
        >
          {t('request:interaction.button.editRequest')}
        </Button>
      )}
      {(canDelete(userPermissions, PERMISSIONS.REQUEST.REQUEST) || isPrivileged(user)) && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={t('request:content.description.youCanOnlyDeleteRejectedRequests')}>
            <InfoOutlined />
          </Tooltip>
          <Button
            id="deleteRequestButton"
            size="small"
            variant="outlined"
            color="primary"
            style={{ textTransform: 'none' }}
            startIcon={<DeleteOutlined />}
            onClick={() => setOpenDelete(true)}
            disabled={
              selectedRequests.length === 0 ||
              selectedRequests.some(request => request.status !== 2)
            }
          >
            {t('request:interaction.button.deleteRequests')}
          </Button>
        </Stack>
      )}
    </Stack>
  )

  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  // Helper to construct files URL from self URL
  const constructFilesUrlFromSelf = (selfUrl: string | undefined): string | undefined => {
    if (!selfUrl) return undefined
    const normalizedSelf = selfUrl.endsWith('/') ? selfUrl : `${selfUrl}/`
    return `${normalizedSelf}files/`
  }

  // collect files to be displayed by the FilesDrawer
  const collectedFiles: FileCollection[] = []
  if (selectedRequest) {
    const requestFiles = Array.isArray(selectedRequest.files) ? selectedRequest.files : []
    // Always construct URL from self to ensure we get only this request's files
    const requestFilesUrl = constructFilesUrlFromSelf(selectedRequest.self)

    // Only process article files if article exists and is not null
    if (selectedRequest.article !== null && selectedRequest.article !== undefined) {
      // Get article self URL - article can be object or string
      const articleSelf =
        typeof selectedRequest.article === 'string'
          ? selectedRequest.article
          : selectedRequest.article.self
      const articleFilesUrl = constructFilesUrlFromSelf(articleSelf)
      let articleFiles: FileInfo[] = []

      // Add article files with article context for permission checking
      // For staff users: we need to check if the article belongs to their organization
      // Since we can't easily compare organizations, we'll mark all article files as potentially
      // from other organizations for staff users. The canDeleteFile function will handle the final check.
      if (
        typeof selectedRequest.article !== 'string' &&
        Array.isArray(selectedRequest.article.files)
      ) {
        articleFiles = selectedRequest.article.files.map((file: FileInfo) => {
          // Ensure each file has the article information for permission checking
          if (!file.article || typeof file.article === 'string') {
            return {
              ...file,
              article: selectedRequest.article, // Add full article object for permission checking
            } as FileInfo
          }
          return file
        })
      }

      if (articleFiles.length > 0 || articleFilesUrl) {
        collectedFiles.push({
          title: t('item:content.heading.articleFiles'),
          files: articleFiles,
          filesUrl: articleFilesUrl,
          downloadOnly: !isPrivileged(user, 'STAFF'),
        })
      }
    }

    // Add Request Files - staff can delete, non-staff can only download
    if (requestFiles.length > 0 || requestFilesUrl) {
      collectedFiles.push({
        title: t('common:content.label.requestFiles'),
        files: requestFiles,
        filesUrl: requestFilesUrl,
        downloadOnly: !isPrivileged(user, 'STAFF'),
      })
    }
  }
  /* ------------------------- Pre render actions end ------------------------- */

  log.debug('selectedRequest ->', selectedRequest, 'CollectedFiles ->', collectedFiles)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <>
      <SmartTable<Request, RequestFilters>
        tableId="requestTable"
        columnsConfig={{
          allColumns: requestTableColumns,
          initialVisibleColumnIndices: [
            'self',
            'article',
            'organization',
            'wishDate',
            'status',
            'priority',
            'editors',
            'action',
          ],
        }}
        queryConfig={{
          dataHook: useRequests,
          initialQueryOptions: requestQueryOptions,
        }}
        queryState={{
          options: requestQueryOptions,
          setOptions: setRequestQueryOptions,
        }}
        selection={{
          state: selectedRequests,
          setState: setSelectedRequests,
          onChange: selected => {
            if (selected.length === 1) {
              const record = selected[0]
              // Mark as read if unread
              if (!record.read) {
                void markRequestAsRead(record)
              }
              setSelectedRequest(record)
            } else if (selected.length === 0) {
              setSelectedRequest(undefined)
            }
          },
        }}
        actions={requestTableActions}
        customInfoPanel={(open, setOpen) => (
          <RequestInfoPanelContent
            setOpenFilesDrawer={setOpenFilesDrawer}
            infoData={selectedRequest}
          />
        )}
        onRow={{
          onClick: record => {
            setSelectedRequests([record])
            setSelectedRequest(record)
          },
        }}
        contextMenuOptions={[
          {
            label: t('common:interaction.button.showFiles'),
            onClick: () => setOpenFilesDrawer(true),
          },
        ]}
        expandable={{
          expandedRowRender: record => (
            <ArticlesExpandedRow requestArticles={record.requestArticles} />
          ),
          rowExpandable: record =>
            Boolean(record.requestArticles && record.requestArticles.length > 0),
        }}
      />
      <RequestDrawer
        open={requestDrawerOpen}
        setOpen={setRequestDrawerOpen}
        formState={{
          state: requestFormInput,
          setState: setRequestFormInput,
        }}
        enableEditMode={true}
      />
      {isPrivileged(user, 'STAFF') && (
        <RequestStaffDrawer
          open={requestStaffDrawerOpen}
          setOpen={setRequestStaffDrawerOpen}
          formState={{
            state: requestStaffFormInput,
            setState: setRequestStaffFormInput,
          }}
          onEditConfirm={handleUpdateRequest}
          onFilesUploaded={handleFilesUploaded}
        />
      )}
      <CreateOfferDrawer
        open={offerDrawerOpen}
        setOpen={setOfferDrawerOpen}
        formState={{
          state: offerFormInput,
          setState: setOfferFormInput,
        }}
        onCreateConfirm={handleCreateOffer}
        setTabValue={setTabValue}
      />
      {isPrivileged(user, 'STAFF') && openCreateRequestSharepointDrawer && (
        <CreateRequestSharepointDrawer
          open={openCreateRequestSharepointDrawer}
          setOpen={setOpenCreateRequestSharepointDrawer}
          selectedRequest={selectedRequest!}
          onConfirm={handleCreateRequestSharepoint}
        />
      )}
      <DeleteModal
        open={openDelete}
        setOpen={setOpenDelete}
        onDelete={handleDeleteRequest}
        name={t('common:content.label.requests')}
      />
      {openFilesDrawer && (
        <FilesDrawer
          open={openFilesDrawer}
          setOpen={setOpenFilesDrawer}
          files={collectedFiles}
          title={t('common:content.label.files')}
          refreshKey={filesDrawerRefreshKey}
          downloadOnly={!isPrivileged(user, 'STAFF')}
        />
      )}
    </>
  )
}
